import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Content } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import suggestion from './mention-suggestion';
import 'tippy.js/dist/tippy.css';

interface ConnectedNode {
  id: string;
  label: string;
}

interface TipTapPromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  connectedNodes: ConnectedNode[];
  placeholder?: string;
  className?: string;
}

export function TipTapPromptEditor({
  value,
  onChange,
  connectedNodes,
  placeholder = 'Enter text...',
  className = '',
}: TipTapPromptEditorProps) {
  const safeValue = typeof value === 'string' ? value : '';
  // Use ref to always have access to latest connectedNodes
  const connectedNodesRef = useRef(connectedNodes);
  // Track the last synced value to avoid unnecessary external updates
  const lastSyncedValue = useRef(safeValue);
  // Track whether editor currently has any text to control placeholder visibility
  const [hasText, setHasText] = useState(Boolean(safeValue.trim().length));

  // Update ref when connectedNodes changes
  useEffect(() => {
    connectedNodesRef.current = connectedNodes;
  }, [connectedNodes]);

  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const buildContentFromPlainText = useCallback((text: string): Content => {
    const nodes = connectedNodesRef.current || [];
    if (!nodes.length) {
      return `<p>${text || ''}</p>`;
    }

    // Map labels and their aliases (strip leading "#<n> " if present) to nodes
    const labelToNode = new Map<string, ConnectedNode>();
    for (const n of nodes) {
      if (!n.label) continue;
      labelToNode.set(n.label, n);
      const alias = n.label.replace(/^#\d+\s+/, '');
      if (alias && alias !== n.label) {
        // Only add alias if different
        labelToNode.set(alias, n);
      }
    }

    const labels = Array.from(labelToNode.keys());
    // Sort by length desc to prefer longer matches
    labels.sort((a, b) => (b.length || 0) - (a.length || 0));
    const labelAlternation = labels.map((l) => escapeRegExp(l)).join('|');

    if (!labelAlternation) {
      return `<p>${text || ''}</p>`;
    }

    const mentionRegex = new RegExp(`@(${labelAlternation})`, 'g');
    const paragraphs = (text || '').split(/\r?\n/);

    const doc = {
      type: 'doc',
      content: paragraphs.map((line) => {
        const content: Array<Record<string, unknown>> = [];

        let lastIndex = 0;
        let match: RegExpExecArray | null;
        // eslint-disable-next-line no-cond-assign
        while ((match = mentionRegex.exec(line)) !== null) {
          const matchText = match[0];
          const matchLabel = match[1];
          const matchIndex = match.index;

          if (matchIndex > lastIndex) {
            content.push({
              type: 'text',
              text: line.slice(lastIndex, matchIndex),
            });
          }

          const node = labelToNode.get(matchLabel);
          if (node) {
            content.push({
              type: 'mention',
              attrs: { id: node.id, label: node.label },
            });
          } else {
            content.push({ type: 'text', text: matchText });
          }

          lastIndex = matchIndex + matchText.length;
        }

        if (lastIndex < line.length) {
          content.push({ type: 'text', text: line.slice(lastIndex) });
        }

        return {
          type: 'paragraph',
          content: content.length
            ? content
            : [{ type: 'text', text: '' } as Record<string, unknown>],
        };
      }),
    } satisfies Content;

    return doc;
  }, []);

  const editor = useEditor({
    immediatelyRender: false, // Fix SSR hydration issues with Next.js
    extensions: [
      StarterKit.configure({
        // Disable heading, code blocks, etc. - just plain text with mentions
        heading: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: suggestion(() => connectedNodesRef.current),
        renderHTML({ options, node }) {
          return [
            'span',
            {
              ...options.HTMLAttributes,
              class: `${options.HTMLAttributes.class} inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200`,
            },
            `@${node.attrs.label ?? node.attrs.id}`,
          ];
        },
        renderText({ node }) {
          // Ensure mentions are serialized as @NodeName in plain text
          return `@${node.attrs.label ?? node.attrs.id}`;
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      // Update placeholder visibility reactively while typing
      setHasText(editor.getText().trim().length > 0);
    },
    // Sync changes to parent only when user leaves the field (on blur)
    // This prevents constant re-renders and cursor jumping
    onBlur: ({ editor }) => {
      const currentText = editor.getText();
      lastSyncedValue.current = currentText;
      onChange(currentText);
    },
    editorProps: {
      attributes: {
        class: `focus:outline-none min-h-[60px] text-sm ${className}`,
      },
    },
  });

  // Set initial content with mentions reconstructed from plain text
  useEffect(() => {
    if (!editor) return;
    const initial = buildContentFromPlainText(safeValue);
    editor.commands.setContent(initial);
    lastSyncedValue.current = safeValue;
    setHasText(Boolean(safeValue.trim().length));
    // we only want to run once on init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Update editor content only when value changes externally (e.g., from node connections)
  useEffect(() => {
    if (!editor) return;

    // Only update if value changed from external source (not from our own blur event)
    if (safeValue !== lastSyncedValue.current) {
      const currentText = editor.getText();
      // Only set content if it's actually different from what's in the editor
      if (safeValue !== currentText) {
        const contentWithMentions = buildContentFromPlainText(safeValue);
        editor.commands.setContent(contentWithMentions);
        lastSyncedValue.current = safeValue;
        setHasText(Boolean(safeValue.trim().length));
      }
    }
  }, [safeValue, editor, buildContentFromPlainText, value]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className='tiptap-wrapper w-full relative nodrag nopan'
      onMouseDownCapture={(e) => e.stopPropagation()}
      onPointerDownCapture={(e) => e.stopPropagation()}
      onTouchStartCapture={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <EditorContent
        editor={editor}
        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent resize-none break-words dark:border-white/5 dark:bg-slate-700/50 dark:text-white cursor-text'
      />
      {!hasText && placeholder && (
        <div className='pointer-events-none absolute top-2 left-3 text-gray-400 dark:text-gray-500 text-sm'>
          {placeholder}
        </div>
      )}
    </div>
  );
}
